import React, { useEffect, useState } from 'react';
import { Archive, Download, AlertTriangle, CheckCircle, Loader2, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { buildResolveContext, buildFinalRanking } from './TornaiEditor';

// Componente admin per l'archiviazione automatica dell'edizione corrente.
// Legge tutti i dati "correnti" dal DB e li impacchetta in una nuova edizione passata.
// Non elimina automaticamente nulla — solo con checkbox esplicito.
export default function ChiudiEdizione() {
  const [settings, setSettings] = useState({});
  const [calendario, setCalendario] = useState([]);
  const [tournament, setTournament] = useState({});
  const [galleryImgs, setGalleryImgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    anno: new Date().getFullYear(),
    edizione: 'XIV',
    date_range: '',
    descrizione: '',
    copertina: '',
    clean_current: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const [s, cal, tour, gal] = await Promise.all([
          api.getSettings().catch(() => ({})),
          api.getCalendarioFull().catch(() => []),
          api.getTournament().catch(() => ({})),
          api.getGallery ? api.getGallery().catch(() => []) : Promise.resolve([]),
        ]);
        setSettings(s || {});
        setCalendario(Array.isArray(cal) ? cal : []);
        setTournament(tour || {});
        setGalleryImgs(Array.isArray(gal) ? gal : []);
        setForm((f) => ({
          ...f,
          edizione: s?.edition || f.edizione,
          date_range: s?.event_date_range || f.date_range,
          descrizione: s?.event_description || f.descrizione,
          copertina: s?.hero_image || s?.logo_url || f.copertina,
        }));
      } catch {
        toast.error('Errore caricamento dati');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const resolveCtx = buildResolveContext(calendario);

  // Deduce automaticamente i vincitori per categoria dalle finali nel calendario
  const vincitori = ['pulcini-2015', 'pulcini-2016', 'primi-calci'].map((catKey) => {
    const ranking = buildFinalRanking(catKey, calendario, resolveCtx);
    const winner = ranking && ranking[0] ? ranking[0].team : '';
    const finalist = ranking && ranking[1] ? ranking[1].team : '';
    const semi = ranking ? ranking.filter((r) => r.fase === 'semifinale').map((r) => r.team) : [];
    return { categoria: catKey, vincitore: winner, secondo: finalist, semifinalisti: semi };
  }).filter((v) => v.vincitore || v.secondo);

  // Marcatori aggregati
  const marcatori = Object.entries(tournament || {}).flatMap(([cat, t]) =>
    (t?.marcatori || []).map((m) => ({ ...m, categoria: cat }))
  );

  const squadrePartecipanti = (() => {
    const set = new Set();
    Object.values(resolveCtx.matchesByCat || {}).forEach((matches) => {
      matches.forEach((m) => { if (m.squadra1) set.add(m.squadra1); if (m.squadra2) set.add(m.squadra2); });
    });
    return set.size;
  })();

  const downloadBackup = async () => {
    try {
      // Endpoint backup.php scarica JSON completo del DB
      const url = `${window.location.origin}/api/index.php?route=backup`;
      window.open(url, '_blank');
      toast.success('Backup scaricato');
    } catch (e) {
      toast.error('Errore backup: ' + (e?.message || 'sconosciuto'));
    }
  };

  const archivia = async () => {
    if (!form.anno || !form.edizione) return toast.error('Anno ed Edizione obbligatori');
    if (vincitori.length === 0) {
      if (!window.confirm('Nessun vincitore rilevato (le finali potrebbero non essere ancora giocate). Procedi comunque?')) return;
    }
    setRunning(true);
    try {
      // Costruisci l'oggetto edizione
      const payload = {
        anno: Number(form.anno),
        edizione: form.edizione,
        date: form.date_range,
        descrizione: form.descrizione,
        copertina: form.copertina,
        squadrePartecipanti,
        vincitoriCategorie: vincitori,
        highlights: [
          ...marcatori.slice(0, 5).map((m) => `Capocannoniere ${m.calciatore} (${m.reti} reti)`),
        ],
        gallery: galleryImgs.map((g) => g.url || g.src || g).filter(Boolean),
      };
      await api.saveEdition(payload);
      // Se admin ha spuntato pulizia, azzera calendario e tornei
      if (form.clean_current) {
        // Pulisci calendario: elimina tutti i giorni (uno per uno via API se non c'è bulk)
        for (const d of calendario) {
          if (d.id) await api.deleteCalendario(d.id).catch(() => {});
        }
        // Reset tornei: cancella match/marcatori/cartellini di ogni categoria
        const resetTournament = {};
        for (const cat of Object.keys(tournament || {})) {
          resetTournament[cat] = { active: false, matches: [], marcatori: [], cartellini: [] };
        }
        if (Object.keys(resetTournament).length > 0) {
          await api.saveTournament(resetTournament).catch(() => {});
        }
        toast.success('Dati correnti azzerati (calendario + tornei)');
      }
      setDone(true);
      toast.success(`Edizione ${form.edizione} ${form.anno} archiviata!`);
    } catch (e) {
      toast.error('Errore archiviazione: ' + (e?.message || 'sconosciuto'));
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <div className="glass-card p-8 text-center text-white/60"><Loader2 className="animate-spin mx-auto mb-3" /> Caricamento…</div>;

  return (
    <div className="space-y-5" data-testid="chiudi-edizione-panel">
      <div className="glass-card p-5">
        <div className="flex items-center gap-3 mb-2">
          <Archive size={22} className="text-pink-brand" />
          <h2 className="text-white font-bold text-xl">Chiudi Edizione &amp; Archivia</h2>
        </div>
        <p className="text-white/70 text-sm">
          Impacchetta i dati dell'edizione corrente (vincitori dedotti dalle finali, marcatori, gallery, descrizione) in una nuova voce di &quot;Edizioni Passate&quot;.
          Consigliato dopo la finalizzazione di tutti i risultati.
        </p>
      </div>

      <div className="glass-card p-5">
        <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Trophy size={16} className="text-pink-brand" /> Riepilogo edizione</h3>
        <div className="grid sm:grid-cols-2 gap-3 text-sm mb-4">
          <label className="block">
            <span className="text-white/60 text-xs uppercase font-bold tracking-widest">Anno</span>
            <input className="w-full bg-navy-dark border border-white/20 rounded px-3 py-2 text-white mt-1" value={form.anno} onChange={(e) => setForm({ ...form, anno: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-white/60 text-xs uppercase font-bold tracking-widest">Edizione (numero romano)</span>
            <input className="w-full bg-navy-dark border border-white/20 rounded px-3 py-2 text-white mt-1" value={form.edizione} onChange={(e) => setForm({ ...form, edizione: e.target.value })} />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-white/60 text-xs uppercase font-bold tracking-widest">Date (es. 25-31 Maggio 2026)</span>
            <input className="w-full bg-navy-dark border border-white/20 rounded px-3 py-2 text-white mt-1" value={form.date_range} onChange={(e) => setForm({ ...form, date_range: e.target.value })} />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-white/60 text-xs uppercase font-bold tracking-widest">Descrizione</span>
            <textarea className="w-full bg-navy-dark border border-white/20 rounded px-3 py-2 text-white mt-1" rows={3} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-white/60 text-xs uppercase font-bold tracking-widest">Copertina URL</span>
            <input className="w-full bg-navy-dark border border-white/20 rounded px-3 py-2 text-white mt-1" value={form.copertina} onChange={(e) => setForm({ ...form, copertina: e.target.value })} />
          </label>
        </div>

        <div className="bg-navy-dark/60 border border-white/10 rounded p-3 text-sm space-y-1">
          <div className="text-white/60 text-xs uppercase font-bold tracking-widest mb-2">Dati auto-rilevati dal calendario</div>
          <div className="text-white/80">Squadre partecipanti: <strong className="text-white">{squadrePartecipanti}</strong></div>
          <div className="text-white/80">Marcatori totali: <strong className="text-white">{marcatori.length}</strong></div>
          <div className="text-white/80">Foto gallery: <strong className="text-white">{galleryImgs.length}</strong></div>
          <div className="text-white/80 mt-2">Vincitori rilevati:</div>
          {vincitori.length === 0 ? (
            <div className="text-yellow-400 flex items-center gap-1"><AlertTriangle size={12} /> Nessun vincitore rilevato &mdash; finali non ancora giocate?</div>
          ) : (
            <ul className="text-white/90 text-xs pl-3 space-y-0.5">
              {vincitori.map((v, i) => (
                <li key={i}>• <strong>{v.categoria}</strong>: 🥇 {v.vincitore || '?'} · 🥈 {v.secondo || '?'}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="glass-card p-5 space-y-3">
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" className="mt-1" checked={form.clean_current} onChange={(e) => setForm({ ...form, clean_current: e.target.checked })} />
          <span className="text-white/85 text-sm">
            <strong className="text-yellow-400">Pulisci dati correnti</strong> dopo l'archiviazione (calendario + tornei tornano vuoti per la prossima edizione).<br/>
            <span className="text-white/50 text-xs">Attenzione: consigliato solo se hai scaricato il backup.</span>
          </span>
        </label>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={downloadBackup}
            className="flex-1 bg-navy-dark border border-white/20 hover:border-pink-brand text-white px-4 py-3 rounded flex items-center justify-center gap-2 transition-colors"
            data-testid="chiudi-edizione-backup-btn"
          >
            <Download size={16} /> 1. Scarica backup DB
          </button>
          <button
            onClick={archivia}
            disabled={running || done}
            className="flex-1 bg-pink-brand hover:bg-pink-brand/90 disabled:opacity-50 text-white px-4 py-3 rounded flex items-center justify-center gap-2 font-bold transition-colors"
            data-testid="chiudi-edizione-archive-btn"
          >
            {done ? <><CheckCircle size={16} /> Archiviata!</> : running ? <><Loader2 size={16} className="animate-spin" /> Archiviazione…</> : <><Archive size={16} /> 2. Archivia Edizione</>}
          </button>
        </div>
        {done && (
          <div className="bg-green-500/10 border border-green-500/40 text-green-300 text-sm p-3 rounded">
            ✅ Edizione archiviata correttamente. Ora appare in &quot;Edizioni Passate&quot;.
          </div>
        )}
      </div>
    </div>
  );
}
